import { useForm, TimezoneField, convertStringToTimezone } from "@tellescope/forms";
import { useAuth, useTellescopeMutation, useUser } from "@tellescope/tanstack-query";
import { Button, Input, Label, LanguageSelector, PhoneInput } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { AvatarUpload } from "./AvatarUpload";

export interface AccountSectionProps {
  userId?: string;
}

export const AccountSection = ({ userId: providedUserId }: AccountSectionProps) => {
  const { t } = useTranslation("auth");
  const { currentUser } = useAuth();
  const providedUser = useUser(providedUserId ?? "", !!providedUserId);
  const user = providedUserId ? providedUser : currentUser;
  const userId = user?.id;

  const updateUserMutation = useTellescopeMutation({
    model: "users",
    method: "updateOne",
  });

  const form = useForm({
    fields: {
      email: {
        type: "email",
        required: true,
        initialValue: user?.email,
      },
      fname: {
        type: "text",
        required: true,
        initialValue: user?.fname,
      },
      lname: {
        type: "text",
        initialValue: user?.lname,
      },
      displayName: {
        type: "text",
        initialValue: user?.displayName,
      },
      internalDisplayName: {
        type: "text",
        initialValue: user?.internalDisplayName,
      },
      phone: {
        type: "phone",
        initialValue: user?.phone,
      },
      timezone: {
        type: "timezone",
        initialValue: user?.timezone,
      },
    },
    validateOn: "change",
    onSubmit: (values) => {
      if (!userId) return;

      updateUserMutation.mutateAsync({
        id: userId,
        ...values,
        timezone: values.timezone ? convertStringToTimezone(values.timezone) : undefined,
      });
    },
  });

  if (!userId || !user) return null;

  return (
    <div className="flex flex-col h-full overflow-y-auto p-2">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-semibold">{t("Profile Picture")}</h3>
          <AvatarUpload userId={userId} />
        </div>
      </div>
      <form className="flex flex-col pt-2 pb-6" {...form.getFormProps()}>
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-semibold">{t("Profile Settings")}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-email">{t("Email")}</Label>
              <Input id="account-email" type="email" {...form.getFieldProps("email")} disabled />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-phone">{t("Phone")}</Label>
              <PhoneInput
                id="account-phone"
                {...form.getFieldProps("phone")}
                onChange={(newVal) => form.setFieldValue("phone", newVal)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-fname" required>
                {t("First Name")}
              </Label>
              <Input id="account-fname" {...form.getFieldProps("fname")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-lname" required>
                {t("Last Name")}
              </Label>
              <Input id="account-lname" {...form.getFieldProps("lname")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-displayName">{t("Display Name (Patient Facing)")}</Label>
              <Input id="account-displayName" {...form.getFieldProps("displayName")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-internalDisplayName">{t("Display Name (Team Facing)")}</Label>
              <Input id="account-internalDisplayName" {...form.getFieldProps("internalDisplayName")} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="account-timezone">{t("Timezone")}</Label>
              <TimezoneField
                id="account-timezone"
                {...form.getFieldProps("timezone")}
                onChange={(newVal) => form.setFieldValue("timezone", newVal)}
                className="w-full"
              />
            </div>
          </div>
        </div>
        {form.isDirty && (
          <div className="flex gap-2 items-center justify-end mt-2">
            <Button onClick={() => form.resetForm()} variant="outline" loading={updateUserMutation.isPending}>
              {t("Cancel")}
            </Button>
            <Button type="submit" disabled={!form.isValid} loading={updateUserMutation.isPending}>
              {t("Save")}
            </Button>
          </div>
        )}
      </form>
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold">{t("Language & Localization")}</h3>
        <div className="flex flex-col gap-2">
          <Label htmlFor="account-internalDisplayName">{t("Language")}</Label>
          <LanguageSelector className="w-min" />
        </div>
      </div>
    </div>
  );
};
